import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { loadStripe } from '@stripe/stripe-js';
import { messageOf, unwrap } from '@truecare/api-client';
import { Alert, Badge, Button, Card } from '@truecare/ui';
import type { PaymentMethodResponse } from '@truecare/schemas';
import { api } from '../api';
import { SUPPORT_EMAIL } from '../support-email';
import { AddPaymentMethodModal } from './AddPaymentMethodModal';

/**
 * Homecare payment-method management.
 *
 * Self-service add / make-default / remove for the card(s) charged on
 * completed visits. The publishable key comes from the backend config
 * endpoint (single source of truth) so Stripe Elements can tokenize in the
 * browser; when Stripe isn't configured the key is null and the add
 * affordance is replaced with a "contact us" line.
 *
 * A default card is required before a STRIPE-mode client can book (the
 * server enforces the same gate) — the empty state says so.
 */
export function HomecarePaymentMethods() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  // Audit M3 (2026-09-01) — the card Remove deleted with NO confirmation (the
  // Recipients Remove has a two-step confirm; this was the inconsistent path).
  // Two-step inline confirm: first click arms, second confirms.
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const configQ = useQuery({
    queryKey: ['homecare', 'payment-config'],
    queryFn: () => unwrap(api.GET('/api/v1/me/homecare/payment-methods/config')),
    staleTime: Infinity,
  });
  const listQ = useQuery({
    queryKey: ['homecare', 'payment-methods'],
    queryFn: () => unwrap(api.GET('/api/v1/me/homecare/payment-methods')),
  });

  const publishableKey = configQ.data?.publishableKey ?? null;
  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey],
  );

  const setDefaultMut = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST('/api/v1/me/homecare/payment-methods/{id}/set-default', {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['homecare'] }),
    onError: (e) =>
      setRowError(messageOf(e, 'Could not update the default card.')),
  });

  const removeMut = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE('/api/v1/me/homecare/payment-methods/{id}', {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['homecare'] }),
    onError: (e) =>
      setRowError(messageOf(e, 'Could not remove that card.')),
  });

  const items = listQ.data?.items ?? [];
  const defaultCard = items.find((pm: PaymentMethodResponse) => pm.isDefault);
  const hasDefault = defaultCard !== undefined;
  const defaultExpired = defaultCard?.isExpired ?? false;
  const busy = setDefaultMut.isPending || removeMut.isPending;

  // Audit L3 (2026-09-01) — billing = gold rail (tone-by-meaning), matching the
  // /profile payment card; was defaulting to forest here.
  return (
    <Card accent="left" tone="gold" className="mb-6">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-heading text-lg text-forest">Payment methods</h3>
          <p className="mt-1 font-body text-sm text-ink-muted">
            Visit charges are applied automatically to your default card.
          </p>
        </div>
        {stripePromise && (
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => {
              setRowError(null);
              setAddOpen(true);
            }}
            data-testid="add-payment-method-open"
          >
            Add card
          </Button>
        )}
      </div>

      {listQ.isLoading && (
        <p className="mt-4 font-body text-sm text-ink-muted">
          Loading your payment methods…
        </p>
      )}

      {listQ.isError && (
        <Alert tone="danger" title="Couldn't load your payment methods" className="mt-4">
          Please refresh the page. If this keeps happening, contact us at{' '}
          {SUPPORT_EMAIL}.
        </Alert>
      )}

      {rowError && (
        <Alert tone="danger" className="mt-4" data-testid="payment-method-row-error">
          {rowError}
        </Alert>
      )}

      {items.length > 0 && !hasDefault && (
        <Alert tone="warning" className="mt-4" data-testid="payment-method-no-default">
          None of your cards is set as the default. Choose “Make default” on a card
          below to enable booking.
        </Alert>
      )}

      {defaultExpired && (
        <Alert tone="warning" className="mt-4" data-testid="payment-method-default-expired">
          Your default card has expired. Add a new card to enable booking — visit
          charges can’t be applied to an expired card.
        </Alert>
      )}

      {listQ.data && items.length === 0 && (
        <div className="mt-4" data-testid="payment-method-empty">
          <p className="font-body text-sm text-ink-muted">
            No card on file yet. Add one before booking your first visit — charges
            are applied automatically to your default card.
          </p>
          {!stripePromise && (
            <p className="mt-2 font-body text-sm text-ink-muted">
              Online card entry is temporarily unavailable. Please contact us at{' '}
              {SUPPORT_EMAIL} to set up payment.
            </p>
          )}
        </div>
      )}

      {items.length > 0 && (
        <ul className="mt-4 flex flex-col gap-2">
          {items.map((pm: PaymentMethodResponse) => (
            <li
              key={pm.id}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-md border border-border px-4 py-3"
              data-testid={`payment-method-row-${pm.id}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-body text-sm font-semibold text-ink">
                  {pm.brand} •••• {pm.last4}
                </span>
                {pm.isDefault && (
                  <Badge tone="forest" variant="soft">
                    Default
                  </Badge>
                )}
                {pm.isExpired && (
                  <Badge tone="danger" variant="solid">
                    Expired
                  </Badge>
                )}
                <span className="font-body text-xs text-ink-muted tabular-nums">
                  Expires {String(pm.expMonth).padStart(2, '0')}/{pm.expYear}
                </span>
              </div>
              <div className="flex flex-nowrap items-center gap-2">
                {/* Don't offer "Make default" on an expired card — promoting it
                    would immediately re-block booking (the gate needs a
                    non-expired default). The client should add a new card. */}
                {!pm.isDefault && !pm.isExpired && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      setRowError(null);
                      setDefaultMut.mutate(pm.id);
                    }}
                    data-testid={`payment-method-set-default-${pm.id}`}
                  >
                    Make default
                  </Button>
                )}
                {confirmRemoveId === pm.id ? (
                  <>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      disabled={busy}
                      onClick={() => {
                        setRowError(null);
                        setConfirmRemoveId(null);
                        removeMut.mutate(pm.id);
                      }}
                      data-testid={`payment-method-remove-confirm-${pm.id}`}
                    >
                      Confirm remove
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => setConfirmRemoveId(null)}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      setRowError(null);
                      setConfirmRemoveId(pm.id);
                    }}
                    data-testid={`payment-method-remove-${pm.id}`}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {stripePromise && (
        <AddPaymentMethodModal
          open={addOpen}
          stripePromise={stripePromise}
          // DA-M4 fix: only auto-default the new card when there's no USABLE
          // default (no default at all, or the current one is expired). Otherwise
          // add it as a backup — never silently override the client's chosen
          // default card (which would redirect their auto-charges). Promotion of a
          // backup is explicit via "Make default" above.
          setAsDefault={!hasDefault || defaultExpired}
          onDone={() => setAddOpen(false)}
          onCancel={() => setAddOpen(false)}
        />
      )}
    </Card>
  );
}
